import React from 'react';
import styled, { CSSProperties } from 'styled-components/macro';
import { useField } from 'formik';
import { FormikErrorMessage } from './FormikErrorMessage';
import { FormikLabel } from './FormikLabel';
import { Flex, FlexProps } from '../styles/layout';

const Textarea = styled.textarea`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  width: 100%;
  height: 100px;
  padding: 8px 12px;
  min-height: 100px;
  max-height: 300px;
  border-radius: 4px;
  box-sizing: border-box;
  border: solid 1px #d0d3d9;
  resize: vertical;
  &:focus {
    outline: none;
    border: solid 1px ${({ theme }) => theme.colors.neutral[40]};
  }
`;

const CharCounter = styled.span`
  position: absolute;
  bottom: 12px;
  right: 12px;
  color: ${props => props.theme.colors.neutral[100]};
  pointer-events: none;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  letter-spacing: 0px;
  opacity: 0.6;
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

interface CustomTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  label: string;
  isRequired: boolean;
  showCounter?: boolean;
  maxLength?: number;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties;
    inputWrapper?: FlexProps;
    mainWrapper?: FlexProps;
    textAreaStyles?: CSSProperties;
  };
}

export const FormikTextAreaField = ({
  label,
  isRequired,
  extendStyles,
  showCounter = false,
  maxLength,
  ...props
}: CustomTextareaProps) => {
  const [field, meta] = useField(props);
  return (
    <Flex {...extendStyles?.mainWrapper} gap='4px'>
      <FormikLabel
        label={label}
        name={props.name}
        isRequired={isRequired}
        disabled={!!props.disabled}
        labelStyles={extendStyles?.labelStyles}
      />
      <Flex {...extendStyles?.inputWrapper} gap='4px'>
        <Wrapper>
          <Textarea
            style={extendStyles?.textAreaStyles}
            {...field}
            {...props}
            id={props.name}
          />
          {showCounter && maxLength && (
            <CharCounter>
              {String(field.value).length}/{maxLength}
            </CharCounter>
          )}
        </Wrapper>
        <FormikErrorMessage meta={meta} extendStyles={extendStyles} />
      </Flex>
    </Flex>
  );
};
